import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatUsd, localeUI } from '../lib/format';
import { useI18n } from '../lib/i18n';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h2 className="card-title">{title}</h2>
      {children}
    </div>
  );
}

export function Mortgage() {
  const { t } = useI18n();
  const [rows, setRows] = useState<
    { id: string; claim_number: string | null; amount_cents: string; mortgage_status: string }[]
  >([]);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get<{ checks: typeof rows }>('/api/mortgage').then((r) => setRows(r.checks)).catch(() => setErr(true));
  }, []);
  if (err) return <div className="card">{t('common.needManager')}</div>;
  return (
    <Section title={t('sec.mortgage')}>
      {rows.length === 0 ? (
        <div className="muted">{t('sec.noMortgage')}</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Claim</th>
              <th>{t('sec.mortgageStatus')}</th>
              <th className="num">{t('common.amount')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{r.claim_number || `#${r.id}`}</td>
                <td><span className="badge orange">{r.mortgage_status}</span></td>
                <td className="num">{formatUsd(r.amount_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

export function ReviewInvoice() {
  const { t } = useI18n();
  const [rows, setRows] = useState<
    { id: string; claim_id: string; invoice_number_per_claim: number; fee_billed_this_invoice_cents: string;
      submitted_by: string | null; created_by: string | null; submitted_at: string | null;
      claim_number: string | null; homeowner_name: string | null }[]
  >([]);
  const [err, setErr] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function load() {
    try {
      setRows((await api.get<{ invoices: typeof rows }>('/api/invoices/review')).invoices);
    } catch {
      setErr(true);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function approve(invId: string) {
    await api.post(`/api/invoices/${invId}/approve`);
    setMsg('OK');
    await load();
  }

  async function notApprove(invId: string) {
    const reason = window.prompt(t('cd.notApprovedPrompt'));
    if (!reason || !reason.trim()) return;
    await api.post(`/api/invoices/${invId}/not-approve`, { reason: reason.trim() });
    setMsg('→ Invoice to do');
    await load();
  }

  if (err) return <div className="card">{t('common.needManager')}</div>;
  return (
    <Section title={t('sec.review')}>
      {msg && <div style={{ color: 'var(--green)', fontSize: 'var(--fs-sm)', marginBottom: 8 }}>{msg}</div>}
      {rows.length === 0 ? (
        <div className="muted">{t('sec.noReview')}</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('cd.colInvoice')}</th>
              <th>{t('sec.preparedBy')}</th>
              <th className="num">{t('sec.feeToCharge')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <Link to={`/claims/${r.claim_id}`}>{t('cd.colInvoice')} {r.invoice_number_per_claim}</Link>
                  {(r.claim_number || r.homeowner_name) && (
                    <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{r.homeowner_name ?? ''}{r.claim_number ? ` · #${r.claim_number}` : ''}</div>
                  )}
                </td>
                <td>
                  {r.submitted_by ?? r.created_by ?? <span className="muted">—</span>}
                  {r.submitted_at && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{new Date(r.submitted_at).toLocaleDateString(localeUI())}</div>}
                </td>
                <td className="num">{formatUsd(r.fee_billed_this_invoice_cents)}</td>
                <td style={{ display: 'flex', gap: 6 }}>
                  <button className="btn sm" onClick={() => approve(r.id)}>{t('cd.approve')}</button>
                  <button className="btn ghost sm" style={{ color: 'var(--red)' }} onClick={() => notApprove(r.id)}>{t('cd.notApprove')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

export function ChecksReceived() {
  const { t } = useI18n();
  const [rows, setRows] = useState<{ id: string; amount_cents: string; status: string; payable_to: string }[]>([]);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get<{ checks: typeof rows }>('/api/checks').then((r) => setRows(r.checks)).catch(() => setErr(true));
  }, []);
  if (err) return <div className="card">{t('common.needManager')}</div>;
  const badge = (s: string) => (s === 'available' ? 'green' : s === 'bounced' ? 'red' : 'gray');
  return (
    <Section title={t('sec.checks')}>
      {rows.length === 0 ? (
        <div className="muted">{t('cd.noChecks')}</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('sec.check')}</th>
              <th>{t('cd.colPayableTo')}</th>
              <th>{t('common.status')}</th>
              <th className="num">{t('common.amount')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>#{r.id}</td>
                <td className="muted">{r.payable_to}</td>
                <td><span className={`badge ${badge(r.status)}`}>{r.status}</span></td>
                <td className="num">{formatUsd(r.amount_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

export function InvoiceTodo() {
  const { t } = useI18n();
  return <Section title={t('sec.invoiceTodoTitle')}><div className="muted">{t('sec.invoiceTodoDesc')}</div></Section>;
}

export function WaitingClear() {
  const { t } = useI18n();
  return <Section title={t('sec.waitingClearTitle')}><div className="muted">{t('sec.waitingClearDesc')}</div></Section>;
}
